import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  Controls,
  Background,
  ConnectionLineType,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import theme from 'theme';
import { Plan } from 'types';
import { MapNode, PlanNode, WorkspaceNode } from 'components/MapComponents';
import { layoutElements } from './PlanMapLayout';
import AddChildNode from './AddChildNode';
// import { useHistory, useLocation } from 'react-router-dom';
// import { Base64 } from 'js-base64';

const nodeTypes = {
  workspace: WorkspaceNode,
  plan: PlanNode,
  addChild: AddChildNode,
};

interface Props {
  initialTree: Record<string, MapNode>;
  rootId: string;
  /** true to get all plans, false just returns in planning */
  getAllChildren: boolean;
  fullScreenEnabled: boolean;
}

function PlanMap(props: Props) {
  const { initialTree, rootId, getAllChildren, fullScreenEnabled } = props;
  const [tree, setTree] = useState(initialTree); // Store tree structure
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowInstance = useReactFlow();
  // const location = useLocation();
  // const history = useHistory();

  // const mapState = useMemo(() => {
  //   const searchParams = new URLSearchParams(location.search);
  //   const mapStateHash = searchParams.get('mapState');
  //   let mapState: { [key: string]: any } = {};
  //   if (mapStateHash) {
  //     try {
  //       mapState = JSON.parse(Base64.decode(mapStateHash));
  //     } catch (e) {}
  //   }
  //   return mapState;
  // }, [location.search]);

  const toggleChildren = useCallback(
    (nodeId, expanded) => {
      //const expandedPlans = mapState['expandedPlans'] ?? {};
      const expandedPlans: Record<string, boolean> = {};
      setTree((prevTree) => {
        const prevNode = prevTree[nodeId];
        const childPlans: Plan[] = prevNode.meta.childPlans;
        expandedPlans[nodeId] = expanded;

        if (expanded) {
          // create child nodes
          const reportNodes: Record<string, MapNode> = childPlans.reduce((acc, childPlan) => {
            // const childExpanded = mapState['expandedPlans'] && mapState['expandedPlans'][childPlan.id];
            const childExpanded = false;
            expandedPlans[childPlan.id] = childExpanded;
            acc[childPlan.id] = {
              id: childPlan.id,
              meta: { expanded: false, contentExpanded: true, plan: childPlan, children: [], getAllChildren },
              type: 'plan',
              name: childPlan.title,
              children: [],
              parents: [nodeId],
            };
            return acc;
          }, {} as Record<string, MapNode>);
          // add child ids to parent
          const children = [...childPlans.map((child) => child.id), `${nodeId}-add`];
          const newTree = {
            ...prevTree,
            ...reportNodes,
            [nodeId]: { ...prevNode, children, meta: { ...prevNode.meta, expanded } },
            [`${nodeId}-add`]: {
              id: `${nodeId}-add`,
              meta: { plan: prevNode.meta.plan },
              type: 'addChild',
              name: `${prevNode.id}-add`,
              children: [],
              parents: [prevNode.id],
            },
          };
          return newTree;
        } else {
          const newTree = {
            ...prevTree,
            [nodeId]: { ...prevNode, children: [], meta: { ...prevNode.meta, expanded } },
          };
          return newTree;
        }
      });
    },
    [getAllChildren],
  );

  const addChildPlansToParent = useCallback(
    (nodeId, childPlans) => {
      // const expanded = mapState['expandedPlans'] && mapState['expandedPlans'][nodeId];
      const expanded = tree[nodeId] ? tree[nodeId].meta.expanded : false;
      setTree((prevTree) => {
        const prevNode = prevTree[nodeId];
        return {
          ...prevTree,
          [nodeId]: { ...prevNode, meta: { ...prevNode.meta, childPlans, expanded } },
        };
      });
      if (expanded) {
        toggleChildren(nodeId, true);
      }
    },
    [setTree, tree, toggleChildren],
  );

  const saveContentExpanded = useCallback(
    (nodeId, height, contentExpanded: boolean) => {
      setTree((prevTree) => {
        const prev = prevTree[nodeId];
        const newMeta = { ...prev.meta, loadedHeight: height, contentExpanded };
        return {
          ...prevTree,
          [nodeId]: {
            ...prev,
            meta: newMeta,
          },
        };
      });
    },
    [setTree],
  );

  useEffect(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = layoutElements(
      tree,
      addChildPlansToParent,
      toggleChildren,
      rootId,
      saveContentExpanded,
    );

    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
  }, [tree, addChildPlansToParent, toggleChildren, rootId, saveContentExpanded, setNodes, setEdges]); // Re-run when tree updates

  useEffect(() => {
    const timer = setTimeout(() => {
      reactFlowInstance.fitView({ duration: 500, maxZoom: 0.7, minZoom: 0.7 });
    }, 300); // Adjust the delay (in milliseconds) as needed

    return () => clearTimeout(timer);
  }, [fullScreenEnabled, reactFlowInstance]); // Re-run when tree updates

  // const updateURL = useMemo(() => {
  //   return (mapStateHash: { [key: string]: any }) => {
  //     const encodedHash = Base64.encode(JSON.stringify(mapStateHash));
  //     history.push(location.pathname + '?mapState=' + encodedHash);
  //   };
  // }, [history, location.pathname]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      connectionLineType={ConnectionLineType.SmoothStep}
      fitView
      nodeTypes={nodeTypes}
      snapToGrid={true}
      minZoom={0.1}
      maxZoom={2}
      style={{ background: theme.colors.N3 }}
      proOptions={{ account: 'paid-pro', hideAttribution: true }}
      defaultViewport={{
        x: 0,
        y: 0,
        zoom: 0.6,
      }}
    >
      <Background color={theme.colors.N20} size={2} />
      <Controls />
    </ReactFlow>
  );
}

export default PlanMap;
